import cn from 'classnames'
import { ReactNode, useCallback, useMemo } from 'react'

import { MultiSegmentedControl } from 'cng-common/components/segmentedControl'
import { useFormField } from 'cng-common/features/form'
import { ValidationType } from 'cng-common/features/form/configs'
import { Translation } from 'cng-common/features/translations'

import { getMarketCode, isMarketOff } from '../../../lib/marketStatus'

import styles from './MultiSegmentedFormField.module.scss'

interface IMarketsFormFieldProps {
  formKey: string
  field: string
  label: string
  options: readonly string[]
  /** Stored selection; may contain `${code}:OFF` entries for turned-off markets. */
  defaultValue: string[]
  isRequired?: boolean
  isDisabled?: boolean
  /**
   * Locks the selection: every option is rendered non-interactable while the stored value
   * is preserved (and still submitted). The selected option keeps its active styling — used
   * for market-level users who may only ever apply a BP to their own market.
   */
  isLocked?: boolean
  /** Translation key shown when the field is required and nothing is selected. */
  requiredMessage?: string
  className?: string
  /** Optional helper text rendered between the label and the control. */
  hint?: ReactNode
  /** Optional content rendered below the control. */
  footer?: ReactNode
}

/**
 * Markets variant of the multi-select segmented field. Turned-off markets are stored as
 * `${code}:OFF` entries: they are rendered disabled + strikethrough (so they cannot be
 * re-enabled here) and are preserved untouched in the stored value across edits.
 */
export const MarketsFormField = ({
  formKey,
  field,
  label,
  options,
  defaultValue,
  isRequired,
  isDisabled,
  isLocked,
  requiredMessage,
  className,
  hint,
  footer,
}: IMarketsFormFieldProps) => {
  const onValidate = useCallback(
    (value: string[]) => (isRequired && !value?.length ? (requiredMessage ?? '') : ''),
    [isRequired, requiredMessage]
  )

  const {
    value = [],
    handleChange,
    validationMessage,
  } = useFormField<string[]>({
    formKey,
    field,
    defaultValue,
    onValidate,
    isDisabled,
    validationType: ValidationType.ONCHANGE,
  })

  // Off entries are kept verbatim and merged back on every change; only the active (plain)
  // codes are handed to the control as the editable selection.
  const offEntries = useMemo(() => value.filter(isMarketOff), [value])
  const offCodes = useMemo(() => offEntries.map(getMarketCode), [offEntries])
  const activeCodes = useMemo(() => value.filter((entry) => !isMarketOff(entry)), [value])

  const handleSelect = useCallback(
    (keys: string[]) => {
      handleChange([...keys, ...offEntries])
    },
    [handleChange, offEntries]
  )

  return (
    <div className={cn(styles.root, className, { [styles.locked]: isLocked })}>
      <div className={cn(styles.label, { [styles.required]: isRequired })}>
        <Translation text={label} span />
      </div>

      {hint && <div className={styles.hint}>{hint}</div>}

      <MultiSegmentedControl
        size="small"
        useItemKeys
        selectedItems={activeCodes}
        onMultiItemSelect={handleSelect}
        isDisabled={isDisabled}
        variant="light"
        rounded="rounded-full"
        backgroudn="bg-transparent"
        noWrap
      >
        {options.map((option) => {
          const isOff = offCodes.includes(option)

          return (
            // Wrap in a <span> so the Item does not treat the string as a translation key.
            <MultiSegmentedControl.Item
              key={option}
              itemKey={option}
              isDisabled={isOff || isLocked}
            >
              <span className={cn({ [styles.offOption]: isOff })}>{option}</span>
            </MultiSegmentedControl.Item>
          )
        })}
      </MultiSegmentedControl>

      {validationMessage && (
        <div className={styles.validationMessage}>
          <Translation text={validationMessage} span />
        </div>
      )}

      {footer && <div className={styles.footer}>{footer}</div>}
    </div>
  )
}

MarketsFormField.displayName = 'MarketsFormField'
